/**
 * Shared formatting and link helpers for the Ontology screens.
 *
 * The href builders live here rather than in the components because the agents drawer in
 * OperatorsMarketplace links to the same places — one definition keeps a link built from
 * the drawer and a link built from a browse row pointing at the same route.
 */

export type OntologyKind = 'concepts' | 'policies' | 'parameters';

/**
 * What each collection is CALLED to a user. Display only — the keys stay `concepts`,
 * `policies` and `parameters` in every route, API path and filter, because those are the
 * control plane's names for them.
 *
 * "Concepts" was ontology-engineering vocabulary for a collection an AML investigator
 * reads as red flags, typologies and thresholds. Renaming the tab to "Risk Library" is
 * only half the fix; CONCEPT_CATEGORIES below is the other half.
 */
export const KIND_LABEL: Record<OntologyKind, string> = {
    concepts: 'Risk Library',
    policies: 'Policies',
    parameters: 'Thresholds',
};

/** Singular, for a detail page heading or a button. */
export const KIND_SINGULAR: Record<OntologyKind, string> = {
    concepts: 'entry',
    policies: 'policy',
    parameters: 'threshold',
};

/**
 * The singular resource name POST /ontology/validate expects.
 *
 * Deliberately separate from KIND_SINGULAR, which is display copy and now says "entry" and
 * "threshold". These are WIRE values and must not follow a wording change — one edit to a
 * user-facing noun would otherwise start sending `resource: 'entry'` to the control plane.
 */
export const VALIDATE_RESOURCE: Record<OntologyKind, string> = {
    concepts: 'concept',
    policies: 'policy',
    parameters: 'parameter',
};

/**
 * The identity field for each kind.
 *
 * MUST be selected by kind, never by a `a || b || c` fallback across the three. A
 * parameter document carries BOTH `parameter_code` and `concept_code` — every one of
 * them, since a parameter's identity is readable and its concept is what it binds to —
 * so a precedence chain silently reads the concept code off a parameter row and builds a
 * URL for a parameter that does not exist.
 */
export const CODE_FIELD: Record<OntologyKind, string> = {
    concepts: 'concept_code',
    policies: 'policy_code',
    parameters: 'parameter_code',
};

/** A document's own identity, for the kind of list it came from. */
export const codeOf = (
    doc: Record<string, any> | null | undefined,
    kind: OntologyKind,
): string | null => (doc ? (doc[CODE_FIELD[kind]] ?? null) : null);

/**
 * Codes carry dots and colons (`role.pep`, `alert_type:structuring`), which are legal in
 * a path segment, so they are addressable once encoded. A code containing a SLASH is not:
 * it would split into two segments and route to nothing. Such a code renders as plain
 * text rather than a dead link.
 */
export const isLinkableCode = (code?: string | null): code is string =>
    typeof code === 'string' && code.trim() !== '' && !code.includes('/');

/**
 * Decode a code taken from a route param.
 *
 * React Router already decodes params, so calling decodeURIComponent unconditionally
 * would double-decode: a concept legitimately containing '%20' in its code would come
 * back as a space and then fail to match anything. Only decode when the value still looks
 * encoded, and never throw on a malformed sequence.
 */
export const safeDecodeCode = (raw?: string): string => {
    if (!raw) return '';
    if (!/%[0-9A-Fa-f]{2}/.test(raw)) return raw;
    try {
        return decodeURIComponent(raw);
    } catch {
        return raw;
    }
};

const detailHref = (kind: OntologyKind, code?: string | null): string | null =>
    isLinkableCode(code) ? `#/ontology/${kind}/${encodeURIComponent(code)}` : null;

export const conceptHref = (code?: string | null) => detailHref('concepts', code);
export const policyHref = (code?: string | null) => detailHref('policies', code);
export const parameterHref = (code?: string | null) => detailHref('parameters', code);

/**
 * The Documents tab id.
 *
 * A tab and a route segment, but NOT an OntologyKind — source documents are read-only and
 * have no create/approve/supersede path, so they must stay out of KINDS, apiForKind and
 * codeOf. Same separation `coverage` already has, and for the same reason: putting them in
 * would grow lifecycle buttons that POST to routes the control plane does not serve.
 */
export const DOC_KIND = 'documents';

/**
 * A source document's detail page.
 *
 * Codes carry dots ('FINCEN.FIN-2019-G001') but never slashes, so they address as one path
 * segment exactly as concept and policy codes do.
 */
export const documentHref = (code?: string | null): string | null =>
    isLinkableCode(code) ? `#/ontology/${DOC_KIND}/${encodeURIComponent(code)}` : null;

/** The registry browse screen, with filters pre-applied. */
export const documentsBrowseHref = (
    filters: Record<string, string | number | undefined | null> = {},
): string => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== '') {
            query.set(key, String(value));
        }
    }
    const qs = query.toString();
    return `#/ontology/${DOC_KIND}${qs ? `?${qs}` : ''}`;
};

/**
 * The Coverage tab, opened on one finding.
 *
 * `coverage` is a tab rather than an OntologyKind, so it cannot go through browseHref —
 * that helper is typed to the three collections on purpose, and widening it would let a
 * non-kind reach apiForKind through a caller that reused the same string.
 *
 * The `finding` parameter is what makes the report addressable: a banner elsewhere can say
 * "68 controls have no threshold" in one line and hand the whole list to the screen that
 * already renders it named, linked and grouped, instead of reprinting it in a strip above
 * a table.
 */
export const COVERAGE_KIND = 'coverage';

/** Your vocabulary: a tab and a route segment, not an OntologyKind — see COVERAGE_KIND. */
export const VOCABULARY_KIND = 'vocabulary';

/** One detection scenario on the Your vocabulary tab. */
export const scenarioVocabularyHref = (code?: string | null): string =>
    (code ? `#/ontology/${VOCABULARY_KIND}?scenario=${encodeURIComponent(code)}` : `#/ontology/${VOCABULARY_KIND}`);

/**
 * The Policy Updates tab (change sets, in the engine's terms): policy documents ingested into the library, and the proposals they
 * open for review. A tab and a route segment but not an OntologyKind — a change set is
 * decided, not created or approved through apiForKind — and its rows open their own review
 * drawer rather than OntologyPreviewDrawer.
 */
export const CHANGE_SETS_KIND = 'change-sets';

export const coverageFindingHref = (findingId?: string | null): string =>
    (findingId
        ? `#/ontology/${COVERAGE_KIND}?finding=${encodeURIComponent(findingId)}`
        : `#/ontology/${COVERAGE_KIND}`);

// ─── Source document vocabulary ──────────────────────────────────────────────

/** What kind of instrument this is, in the words a compliance officer uses. */
export const REF_TYPE_LABEL: Record<string, string> = {
    regulatory_guidance: 'Guidance',
    statute: 'Statute',
    regulation: 'Regulation',
    standard: 'Standard',
    internal_policy: 'Internal policy',
    procedure: 'Procedure',
};

/** Published by someone else, or written here. */
export const DOC_CLASS_LABEL: Record<string, string> = {
    external_publication: 'Published',
    internal_document: 'Internal',
};

/**
 * HOW a record ended up inside a document's blast radius.
 *
 * This is the load-bearing label of the impact panel, not decoration. "Cites this
 * document" and "Under an affected policy" are findings of very different strength — the
 * first is an explicit link somebody authored, the last is a transitive consequence that
 * on real data pulls in most of the parameter set — and a reader who cannot tell them
 * apart cannot act on either.
 */
export const REACHED_VIA_LABEL: Record<string, string> = {
    cites: 'Cites this document',
    governs: 'Named in the document',
    governs_concept: 'Governs an affected term',
    sets_concept: 'Tunes an affected term',
    binds: 'Runs an affected term',
    under_policy: 'Under an affected policy',
};

/** An unknown value renders as itself rather than as a blank cell. */
export const refTypeLabel = (value?: string | null): string =>
    (value ? REF_TYPE_LABEL[value] ?? value : '');

export const docClassLabel = (value?: string | null): string =>
    (value ? DOC_CLASS_LABEL[value] ?? value : '');

export const reachedViaLabel = (value?: string | null): string =>
    (value ? REACHED_VIA_LABEL[value] ?? value : '');

/** Which detail route an impact row points at, by the kind the server tagged it with. */
export const hrefForImpactRow = (kind?: string | null, code?: string | null): string | null => {
    if (kind === 'concepts') return conceptHref(code);
    if (kind === 'policies') return policyHref(code);
    if (kind === 'parameters') return parameterHref(code);
    if (kind === DOC_KIND || kind === 'documents') return documentHref(code);
    if (kind === VOCABULARY_KIND) return scenarioVocabularyHref(code);
    return null;
};

/** The browse screen for one kind, with filters pre-applied. */
export const browseHref = (
    kind: OntologyKind,
    filters: Record<string, string | number | undefined | null> = {},
): string => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== null && value !== '') {
            query.set(key, String(value));
        }
    }
    const qs = query.toString();
    return `#/ontology/${kind}${qs ? `?${qs}` : ''}`;
};

// ─── Presentation ────────────────────────────────────────────────────────────

/**
 * A document's tier. `organisation` is the institution's own and the only one it may
 * edit; the other two are platform-level and can only be customised into an org copy.
 */
export const SCOPE_LEVEL_LABEL: Record<string, string> = {
    organisation: 'Your organisation',
    regulatory: 'Regulatory',
    system: 'Platform',
};

export const scopeLevelColor = (scopeLevel?: string): string => {
    switch (scopeLevel) {
        case 'organisation': return '#FFDE59';
        case 'regulatory': return '#7DD3FC';
        default: return '#94A3B8';
    }
};

export const statusColor = (status?: string): string =>
    status === 'approved' ? '#4ADE80' : '#FBBF24';

// ─── Vocabularies ────────────────────────────────────────────────────────────
//
// One label per stored value, used by BOTH the filter dropdown and the table column.
// They were previously two hand-written lists, which is why a dropdown reading
// "Risk signal" sat above a column reading `risk_signal`.

/**
 * `conceptType` — how a concept is read, and what the detail page renders.
 *
 * Ordered by prevalence, which is also the order the rail offers them: controls,
 * signals and metrics are the bulk of the 262.
 *
 * These are the SINGULAR, business-facing names for one entry. The plural category names
 * an investigator navigates by live in CONCEPT_CATEGORIES below — they are not the same
 * strings, because "Red flag" reads correctly in a cell and "Red Flags" reads correctly
 * in a rail, and because one category can cover two types.
 */
export const CONCEPT_TYPE_LABEL: Record<string, string> = {
    control: 'Detection control',
    risk_signal: 'Red flag',
    metric: 'Measure',
    typology: 'Typology',
    case_state: 'Case status',
    listing_topic: 'Screening category',
    severity_band: 'Severity band',
    priority_tier: 'Priority tier',
    // Permitted by the schema but absent from the current data. Labelled anyway, so a
    // column does not fall back to raw snake_case the day one appears.
    segment: 'Customer segment',
    role: 'Role',
    entity: 'Entity',
};

/**
 * The Risk Library's navigation: how the collection is BROKEN UP for a business user.
 *
 * The single word "Concepts" covered a red flag, a laundering typology, a tunable
 * threshold and a case workflow state — objects with different readers (investigator,
 * MLRO, tuning analyst, ops lead) that share nothing but a storage collection. There is no
 * umbrella term that is both accurate and meaningful, so the subsets are surfaced under
 * their own names instead and `concept_type` is promoted from a dropdown to the primary
 * navigation.
 *
 * `types` is a LIST because a category may cover more than one stored type: severity bands
 * and priority tiers are one idea to a user (a scale) and two values in the schema.
 *
 * Ordering is the rail's ordering — by how often someone comes looking for it, which is
 * roughly investigator-first.
 */
export interface ConceptCategory {
    /** Stable id, used as the rail's selection key. Not sent to the API. */
    id: string;
    label: string;
    /** One line of plain English, shown under the heading. This is where the page teaches. */
    subtitle: string;
    /** The `concept_type` values this category selects. */
    types: string[];
}

export const CONCEPT_CATEGORIES: ConceptCategory[] = [
    {
        id: 'red_flags',
        label: 'Red Flags',
        subtitle: 'Behaviours and patterns that suggest something is wrong and warrant a closer look.',
        types: ['risk_signal'],
    },
    {
        id: 'typologies',
        label: 'Typologies',
        subtitle: 'Known laundering methods — the stories the red flags add up to.',
        types: ['typology'],
    },
    {
        id: 'controls',
        label: 'Detection Controls',
        subtitle: 'The tunable checks your agents run. Each one is set by a policy and carries a threshold.',
        types: ['control'],
    },
    {
        id: 'measures',
        label: 'Measures',
        subtitle: 'Quantities computed from activity — the numbers controls and red flags are tested against.',
        types: ['metric'],
    },
    {
        id: 'case_statuses',
        label: 'Case Statuses',
        subtitle: 'The states an alert or case moves through, and what each one means.',
        types: ['case_state'],
    },
    {
        id: 'screening',
        label: 'Screening Categories',
        subtitle: 'Sanctions, PEP and adverse-media topics that screening matches against.',
        types: ['listing_topic'],
    },
    {
        id: 'ratings',
        label: 'Ratings & Priorities',
        subtitle: 'The severity bands and priority tiers used to rank what gets worked first.',
        types: ['severity_band', 'priority_tier'],
    },
    {
        id: 'entities',
        label: 'Customer & Entity Terms',
        subtitle: 'Segments, roles and entity kinds the rest of the library refers to.',
        types: ['segment', 'role', 'entity'],
    },
];

/**
 * The category a stored `concept_type` belongs to, or null for a value no category claims.
 *
 * A NEW type added server-side lands in no category, which is deliberate: it stays visible
 * in "All entries" and in the Type fallback rather than being silently filed under
 * whichever category happens to sort first.
 */
export const categoryForConceptType = (
    conceptType?: string | null,
): ConceptCategory | null => (
    conceptType
        ? CONCEPT_CATEGORIES.find((category) => category.types.includes(conceptType)) ?? null
        : null
);

export const categoryById = (id?: string | null): ConceptCategory | null => (
    id ? CONCEPT_CATEGORIES.find((category) => category.id === id) ?? null : null
);

/**
 * The `?concept_type=` value for a category — comma-joined when it covers several.
 *
 * The control plane's list filter accepts a comma list for concept_type and splits it into
 * an $in. Keeping the JOIN here rather than in the rail means one place to change if that
 * ever stops being true.
 */
export const conceptTypeParam = (category: ConceptCategory): string =>
    category.types.join(',');

/** Which category a `?concept_type=` query value corresponds to, if any. */
export const categoryForParam = (param?: string | null): ConceptCategory | null => {
    if (!param) return null;
    const wanted = param.split(',').map((value) => value.trim()).filter(Boolean).sort();
    if (!wanted.length) return null;
    return CONCEPT_CATEGORIES.find((category) => {
        const have = [...category.types].sort();
        return have.length === wanted.length
            && have.every((value, index) => value === wanted[index]);
    }) ?? null;
};

/** Where a parameter's value came from. */
export const SOURCE_TYPE_LABEL: Record<string, string> = {
    incumbent_config: 'From running config',
    policy: 'From policy',
    calibrated: 'Calibrated',
    inferred: 'Inferred',
    default: 'Platform default',
};

/**
 * Sentence-case fallback for a value no map covers.
 *
 * Both vocabularies can grow — the ontology spec says as much about `refType` and
 * `relevance`, and the concept-type enum has already outgrown what the filter lists. An
 * unmapped value should read as `Some new type`, not vanish and not leak `some_new_type`.
 */
export const humaniseCode = (value: string): string => {
    const words = value.replace(/[_-]+/g, ' ').trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : value;
};

/**
 * What to CALL a record on screen.
 *
 * A code is an address, not a name: `account_type_risk_score_threshold` is unreadable in a
 * list of sixty-eight and tells a compliance officer nothing that "Account type risk score
 * threshold" does not. The stored label wins when there is one; a label that merely repeats
 * the code is treated as absent, because the fallback reads better than it does.
 */
export const displayName = (label?: string | null, code?: string | null): string => {
    const named = (label ?? '').trim();
    if (named && named !== code) return named;
    return code ? humaniseCode(code) : '—';
};

const labelFor = (labels: Record<string, string>) => (value?: string | null): string => {
    if (!value) return '—';
    return labels[value] ?? humaniseCode(String(value));
};

export const conceptTypeLabel = labelFor(CONCEPT_TYPE_LABEL);
export const sourceTypeLabel = labelFor(SOURCE_TYPE_LABEL);

/**
 * Filter options for a subset of a vocabulary, labelled from the shared map.
 *
 * The VALUES stay an explicit list rather than every key of the map: offering a filter
 * for a type no document has would return an empty table and read as a fault. The
 * LABELS come from the map, so the option and the column can no longer disagree.
 */
export const optionsFromLabels = (
    labels: Record<string, string>,
    values: string[],
    allLabel: string,
) => [
    { value: '', label: allLabel },
    ...values.map((value) => ({ value, label: labels[value] ?? humaniseCode(value) })),
];

/**
 * How a parameter actually reached the value it governs (spec 2.4).
 *
 * Worth surfacing rather than hiding: a parameter that binds by `concept_code` reaches
 * every template citing that concept, while one that binds by `parameter_code` reaches
 * exactly one row. Naming a parameter after a threshold does NOT make it apply, and this
 * is where that becomes visible.
 */
export const BINDS_VIA_LABEL: Record<string, string> = {
    applies_to_path: 'by config path',
    parameter_code: 'by parameter code',
    concept_code: 'by concept',
};

/** A parameter's value with its unit, for a table cell. */
export const formatValue = (value: unknown, unit?: string | null): string => {
    if (value === null || value === undefined || value === '') return '—';
    const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
    return unit ? `${text} ${unit}` : text;
};

/** A date for display; an unparseable or absent one renders as an em dash. */
export const formatDate = (value?: string | null): string => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
    });
};

// ─── Parameter grouping ──────────────────────────────────────────────────────

/** The precedence order the engine applies. Lower wins. */
export const SCOPE_LEVEL_PRECEDENCE = ['organisation', 'regulatory', 'system'];

/**
 * Position of a scope level in the precedence order; an unknown level sorts last.
 *
 * This is the ONLY piece of resolution the UI is allowed to reproduce, and only because
 * it is a published constant rather than a computation. Specificity scoring — which
 * decides between two parameters at the SAME level — stays server-side. Resolution
 * already exists twice (control plane and engine); a third copy in the browser is how a
 * screen ends up claiming a threshold the pipeline does not apply.
 */
export const scopeRank = (scopeLevel?: string): number => {
    const rank = SCOPE_LEVEL_PRECEDENCE.indexOf(scopeLevel ?? '');
    return rank === -1 ? SCOPE_LEVEL_PRECEDENCE.length : rank;
};

export interface ParameterGroup<T> {
    key: string;
    /** Best (lowest) scope rank among the group's members, for ordering. */
    rank: number;
    items: T[];
}

/**
 * Group parameters by one of their single-valued parents.
 *
 * Both `policy_code` and `concept_code` are 1:N onto a parameter — every parameter names
 * exactly one of each — which is what makes this grouping unambiguous in either
 * direction. Groups are ordered by the best scope level they contain, so an
 * institution's own thresholds lead.
 */
export const groupParametersBy = <T extends Record<string, any>>(
    parameters: T[],
    key: 'policy_code' | 'concept_code',
    unassignedLabel = '(no policy)',
): ParameterGroup<T>[] => {
    const groups = new Map<string, ParameterGroup<T>>();

    for (const parameter of parameters) {
        const groupKey = parameter[key] || unassignedLabel;
        const existing = groups.get(groupKey);
        const rank = scopeRank(parameter.scope_level);

        if (existing) {
            existing.items.push(parameter);
            existing.rank = Math.min(existing.rank, rank);
        } else {
            groups.set(groupKey, { key: groupKey, rank, items: [parameter] });
        }
    }

    return [...groups.values()].sort(
        (a, b) => a.rank - b.rank || a.key.localeCompare(b.key));
};

/**
 * What a user may do with a document, derived from its tier and status.
 *
 * The three states are mutually exclusive and cover everything:
 *   - a global document can only be CUSTOMISED into an organisation copy;
 *   - an organisation draft can be edited, deleted or approved;
 *   - an organisation approved document can only be SUPERSEDED — editing it in place
 *     would rewrite history, since it may already have governed recorded decisions.
 */
export type LifecycleState = 'global' | 'org_draft' | 'org_approved';

export const lifecycleStateOf = (doc: {
    scope_level?: string;
    status?: string;
} | null | undefined): LifecycleState => {
    if (!doc || doc.scope_level !== 'organisation') return 'global';
    return doc.status === 'approved' ? 'org_approved' : 'org_draft';
};
